import {Dispatch, GetState} from '../app-types';
import {AbortCategory, fetchJson, fetchWithBody, postJsonWithBody} from './fetcher';
import {showError} from '../message-box/message-box-actions';
import {HibuSearchRequest, HibuSearchResponse, ProdDocument} from '../search/search-types';
import {
    addBookmarkAction,
    removeBookmarkAction,
    searchCompletedAction,
    searchFacetsCompletedAction,
    searchHistoryClearedAction,
    searchHistoryLoadedAction,
    setUseAutoCorrection,
} from '../search/duck/search-actions';
import {
    BOOKMARKS_API_ENDPOINT,
    FETCH_DOWNLOAD_FILE_API_ENDPOINT,
    FETCH_PREVIEW_DOCUMENT_API_ENDPOINT,
    FETCH_PREVIEW_FILE_API_ENDPOINT,
    formatHits,
    getActualQueryString,
    getSearchRequest,
    handleSearchError,
    HIGHLIGHT_API_ENDPOINT,
    HISTORY_API_ENDPOINT,
    HTML_EXPORT,
    JSON_EXPORT,
    SAVED_SEARCH_API_ENDPOINT,
} from '../search/search.utils';
import {batch} from 'react-redux';
import {searchPageAttributesMap, SearchPageType} from '../search/search-config';
import {HistoryEntry} from '../search/duck/history-types';
import {isSearchInFavorites} from '../utils/utils';
import {BACKEND_URL} from '../config';
import {SearchCompletedAction} from '../search/duck/search-types';
import {URL_FILTER_PARAM} from '../search/const';

export const SEARCH_BATCH_SIZE = 24;

export function doFetchNextDocuments(
    requestBody: HibuSearchRequest,
) {
    return (dispatch: Dispatch, getState: GetState): undefined | Promise<HibuSearchResponse | undefined> => {
        const state = getState();
        const serviceEndpoint = isSearchInFavorites(state.search.BASIC.sources) ? `/${BOOKMARKS_API_ENDPOINT}/searchDocuments` : `/searchDocuments`;
        return postJsonWithBody<HibuSearchResponse | undefined>(
            serviceEndpoint,
            JSON.stringify(requestBody),
            AbortCategory.SEARCH
        );
    };
}

export function doFetchFacets(
    searchPageType: SearchPageType,
    requestBody: HibuSearchRequest,
) {
    return (dispatch: Dispatch, getState: GetState): undefined | Promise<SearchCompletedAction | undefined> => {
        const state = getState();
        const serviceEndpoint = isSearchInFavorites(state.search.BASIC.sources)
            ? `/${BOOKMARKS_API_ENDPOINT}/searchFacets`
            : '/searchFacets';

        const searchDocumentsResponse = postJsonWithBody<HibuSearchResponse | undefined>(
            serviceEndpoint,
            JSON.stringify(requestBody),
            AbortCategory.FACETS
        );

        return searchDocumentsResponse
            .then((data?: HibuSearchResponse) => {
                if (!data) {
                    return; // Fetch was aborted. The next response will finish updating the state.
                }
                return dispatch(
                    searchFacetsCompletedAction(
                        {
                            facets: data.facets,
                        },
                        searchPageType
                    )
                );
            })
            .catch((error) => {
                return handleSearchError(error, dispatch);
            });
    };
}


export function doSearchRequest(
    searchPageType: SearchPageType,
    requestBody: HibuSearchRequest,
    isSubmittingNewQuery: boolean,
    shouldReplaceSearchResults: boolean,
    replaceUrl?: (searchExpression: string) => void
) {
    return (dispatch: Dispatch, getState: GetState): undefined | Promise<SearchCompletedAction | undefined> => {
        const state = getState();
        const {query, from, size} = requestBody;

        const searchDocumentsServiceEndpoint = isSearchInFavorites(state.search.BASIC.sources) ? `/${BOOKMARKS_API_ENDPOINT}/searchDocuments` : '/searchDocuments';

        const searchState = state.search[searchPageType];
        const resultFor = searchState.resultFor;
        const spellCheckResult = searchState.spellCheckResult;
        const lastSearchedQuery = searchState.lastSearchedQuery;

        const searchDocumentsResponse = postJsonWithBody<HibuSearchResponse | undefined>(
            searchDocumentsServiceEndpoint,
            JSON.stringify(requestBody),
            AbortCategory.SEARCH
        );
        return searchDocumentsResponse
            .then((data?: HibuSearchResponse) => {
                if (!data) {
                    return; // Fetch was aborted. The next response will finish updating the state.
                }

                let nextResultFor = resultFor || '';
                let nextSpellCheckResult = spellCheckResult || '';
                let lastSearched = lastSearchedQuery || '';
                if (isSubmittingNewQuery) {
                    nextResultFor = data.resultFor || '';
                    nextSpellCheckResult = data.spellcheckSuggestions?.length > 0 ? data.spellcheckSuggestions[0].text : spellCheckResult;
                    lastSearched = data.searchRequest ? data.searchRequest.query : query;
                    if (nextResultFor && replaceUrl) {
                        replaceUrl(nextResultFor);
                    }
                }
                return dispatch(
                    searchCompletedAction(
                        {
                            facets: data.facets,
                            docs: formatHits(data.hits),
                            totalFound: data.totalFound,
                            spellCheckResult: nextSpellCheckResult,
                            resultFor: nextResultFor,
                            lastSearchedQuery: lastSearched,
                            nextDocOffset: from + size,
                            shouldReplaceSearchResults: shouldReplaceSearchResults,
                            searchExecutionDurationMs: data.searchExecutionDurationMs,
                        },
                        searchPageType
                    )
                );
            })
            .catch((error) => {
                return handleSearchError(error, dispatch);
            });
    };
}

export function doHTMLExport(type: SearchPageType) {
    return (dispatch: Dispatch, getState: GetState): void => {

        const state = getState();
        const query = getActualQueryString(state.search[type]);

        const request = getSearchRequest(type, getState, query);

        const reportEndpoint = getHtmlExportApiUrl(type, isSearchInFavorites(state.search.BASIC.sources));
        const response = postJsonWithBody(
            reportEndpoint,
            JSON.stringify(request),
        );

        response.then(htmlAsString => {
            const newTab = window.open('', '_blank');
            if (newTab != null && typeof htmlAsString === 'string') {
                newTab.document.open();
                newTab.document.write(htmlAsString);
                newTab.document.close();
            }
        }).catch((error) => {
            console.error(error);
            return dispatch(showError(type, 'search.export.error.general.html'));
        });
    };
}

export function doJsonExport(type: SearchPageType) {
    return (dispatch: Dispatch, getState: GetState): void => {

        const state = getState();
        const query = getActualQueryString(state.search[type]);

        const request = getSearchRequest(type, getState, query);
        dispatch(setUseAutoCorrection(false));

        const reportEndpoint = getJsonExportApiUrl(type, isSearchInFavorites(state.search.BASIC.sources));
        const response = postJsonWithBody(
            reportEndpoint,
            JSON.stringify(request),
        );

        response.then(response => {
            const jsonBlob = new Blob([JSON.stringify(response)], {type: 'application/json'});

            const anchor = document.createElement('a');
            anchor.style.display = 'none';
            anchor.href = URL.createObjectURL(jsonBlob);
            anchor.download = 'data.json';
            document.body.appendChild(anchor);
            anchor.click();

            // Clean up the anchor element and URL object
            return () => {
                document.body.removeChild(anchor);
                URL.revokeObjectURL(anchor.href);
            };
        }).catch((error) => {
            console.error(error);
            return dispatch(showError(type, 'search.export.error.general.json'));
        });
    };
}

export const addBookmark = (type: SearchPageType, doc: ProdDocument) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        dispatch(addBookmarkAction(type, doc));
        const containerName = getState().settings.containerName;
        const bookmark = {
            container: containerName,
            documentId: doc.id,
            timestamp: Date.now(),
        };

        fetchWithBody(getBookmarksApiUrl(type), 'POST', JSON.stringify(bookmark)).catch(() => {
            batch(() => {
                dispatch(showError(type, 'search.error.general.favorites'));
                dispatch(removeBookmarkAction(type, doc));
            });
        });
    };
};

export const removeBookmark = (type: SearchPageType, doc: ProdDocument) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        dispatch(removeBookmarkAction(type, doc));
        const containerName = getState().settings.containerName;
        const bookmark = {
            container: containerName,
            documentId: doc.id,
        };

        fetchWithBody(getBookmarksApiUrl(type), 'DELETE', JSON.stringify(bookmark)).catch(() => {
            batch(() => {
                dispatch(showError(type, 'Bookmark could not be removed.'));
                dispatch(addBookmarkAction(type, doc));
            });
        });
    };
};

export const fetchHistory = (searchPageType: SearchPageType) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        fetchJson<HistoryEntry[]>(getHistoryApiUrl(searchPageType), 'GET')
            .then((data) => {
                dispatch(searchHistoryLoadedAction(searchPageType, data));
            })
            .catch(() => {
                return dispatch(showError(searchPageType, 'search.error.general.historyLoad'));
            });
    };
};

export const clearHistory = (searchPageType: SearchPageType) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        fetchJson<void>(getHistoryApiUrl(searchPageType), 'DELETE')
            .then(() => {
                dispatch(searchHistoryClearedAction(searchPageType));
            })
            .catch(() => {
                return dispatch(showError(searchPageType, 'search.error.general.historyLoad'));
            });
    };
};

export function getHighlightApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, HIGHLIGHT_API_ENDPOINT);
}

function getHtmlExportApiUrl(searchPageType: SearchPageType, isSearchInFavorites: boolean): string {
    return isSearchInFavorites ? getApiUrl(searchPageType, BOOKMARKS_API_ENDPOINT + '/' + HTML_EXPORT) : getApiUrl(searchPageType, HTML_EXPORT);
}

function getJsonExportApiUrl(searchPageType: SearchPageType, isSearchInFavorites: boolean): string {
    return isSearchInFavorites ? getApiUrl(searchPageType, BOOKMARKS_API_ENDPOINT + '/' + JSON_EXPORT) : getApiUrl(searchPageType, JSON_EXPORT);
}

function getBookmarksApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, BOOKMARKS_API_ENDPOINT);
}

function getHistoryApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, HISTORY_API_ENDPOINT);
}

export function getSavedSearchApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, SAVED_SEARCH_API_ENDPOINT);
}

export function getDocumentPreviewApiUrl(searchPageType: SearchPageType): string {
    return getApiUrl(searchPageType, FETCH_PREVIEW_DOCUMENT_API_ENDPOINT);
}

export function getDownloadFileLink(searchPageType: SearchPageType, downloadLink: string): string {
    return `${
        BACKEND_URL + getApiUrl(searchPageType, FETCH_DOWNLOAD_FILE_API_ENDPOINT)
    }?${URL_FILTER_PARAM}=${encodeURIComponent(downloadLink)}`;
}

export function getPreviewLink(searchPageType: SearchPageType, previewLink: string): string {
    return `${BACKEND_URL + getRelativePreviewLink(searchPageType, previewLink)}`;
}

export function getRelativePreviewLink(searchPageType: SearchPageType, previewLink: string): string {
    return `${getApiUrl(searchPageType, FETCH_PREVIEW_FILE_API_ENDPOINT)
    }?${URL_FILTER_PARAM}=${encodeURIComponent(previewLink)}`;
}

function getApiUrl(searchPageType: SearchPageType, endpoint: string): string {
    return searchPageAttributesMap[searchPageType].endpoint + '/' + endpoint;
}
